import { useMemo } from 'react'
import {
  Currency,
  CurrencyAmount,
  Pair,
  Trade,
  TradeType,
  ERC20Token,
} from '@pancakeswap/sdk'
import useSWR from 'swr'
import { Contract } from '@ethersproject/contracts'
import IPancakePairABI from 'config/abi/IPancakePair.json'
import { wrappedCurrency } from '../utils/wrappedCurrency'
import { useActiveChainId } from './useActiveChainId'
import {
  QOMX_FACTORY_ADDRESS,
  QOMX_WETH_ADDRESS,
  isQSwap,
} from '../config/constants/qomx'
import { usePublicClient } from 'wagmi'
import { publicClientToProvider } from '../utils/wagmiEthersAdapters'
import { FAST_INTERVAL } from '../config/constants'

const FACTORY_ABI = [
  {
    inputs: [
      { internalType: 'address', name: '', type: 'address' },
      { internalType: 'address', name: '', type: 'address' },
    ],
    name: 'getPair',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
]

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

/**
 * Calculate trade for a swap involving Q token via QOMX router.
 * Only triggers when Q is one of the tokens. Returns a single-hop trade through QOMX pool.
 */
export function useQOMXTrade(
  currencyIn?: Currency,
  currencyOut?: Currency,
  amount?: CurrencyAmount<Currency>,
  tradeType: TradeType = TradeType.EXACT_INPUT,
): Trade<Currency, Currency, TradeType> | null {
  const { chainId } = useActiveChainId()
  const publicClient = usePublicClient({ chainId })
  const provider = useMemo(() => (publicClient ? publicClientToProvider(publicClient) : undefined), [publicClient])

  const tokenIn = useMemo(() => wrappedCurrency(currencyIn, chainId), [currencyIn, chainId])
  const tokenOut = useMemo(() => wrappedCurrency(currencyOut, chainId), [currencyOut, chainId])

  const isQTrade = useMemo(() => {
    if (!tokenIn || !tokenOut) return false
    return isQSwap(tokenIn.address, tokenOut.address)
  }, [tokenIn, tokenOut])

  // Replace WQOM (QomSwap) with QOMX's WQOM since they're different contracts
  const [resolvedIn, resolvedOut] = useMemo(() => {
    if (!tokenIn || !tokenOut) return [undefined, undefined] as const
    const replaceWqom = (t: ERC20Token) => {
      if (t.symbol === 'WQOM') {
        return new ERC20Token(chainId, QOMX_WETH_ADDRESS, 18, 'WQOM', 'Wrapped QOM')
      }
      return t
    }
    return [replaceWqom(tokenIn), replaceWqom(tokenOut)] as const
  }, [tokenIn, tokenOut, chainId])

  // Fetch pair address + reserves via SWR
  const { data: pairData } = useSWR(
    isQTrade && resolvedIn && resolvedOut && provider
      ? ['qomx-pair', resolvedIn.address, resolvedOut.address]
      : null,
    async () => {
      const factory = new Contract(QOMX_FACTORY_ADDRESS, FACTORY_ABI, provider)
      const pairAddress: string = await factory.getPair(resolvedIn!.address, resolvedOut!.address)
      if (pairAddress === ZERO_ADDRESS) return null

      const pairContract = new Contract(pairAddress, IPancakePairABI, provider)
      const reserves = await pairContract.getReserves()
      return {
        pairAddress,
        reserve0: reserves.reserve0.toString(),
        reserve1: reserves.reserve1.toString(),
      }
    },
    { dedupingInterval: FAST_INTERVAL, refreshInterval: FAST_INTERVAL },
  )

  return useMemo(() => {
    if (!isQTrade || !resolvedIn || !resolvedOut || !pairData || !amount) return null

    const [token0, token1] = resolvedIn.sortsBefore(resolvedOut)
      ? [resolvedIn, resolvedOut]
      : [resolvedOut, resolvedIn]

    const pair = new Pair(
      CurrencyAmount.fromRawAmount(token0, pairData.reserve0),
      CurrencyAmount.fromRawAmount(token1, pairData.reserve1),
    )

    if (tradeType === TradeType.EXACT_INPUT) {
      return Trade.bestTradeExactIn([pair], amount, resolvedOut, { maxHops: 1, maxNumResults: 1 })[0] ?? null
    }
    return Trade.bestTradeExactOut([pair], resolvedIn, amount, { maxHops: 1, maxNumResults: 1 })[0] ?? null
  }, [isQTrade, resolvedIn, resolvedOut, pairData, amount, tradeType])
}
